import React, { useEffect, useMemo, useState } from 'react';
import MainLayout from '../../../app/layouts/MainLayout';
import { Card } from '../../../components/ui/Card';
import { Presentation, ShieldCheck } from 'lucide-react';
import { classTeacherService } from '../../classes/services/classTeacher.service';
import { AssignedClassTeacher } from '../../classes/types/classroom.types';

const TeachersPage = () => {
  const [assignedTeachers, setAssignedTeachers] = useState<AssignedClassTeacher[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    setIsLoading(true);
    classTeacherService
      .listAssignedTeachers()
      .then((items) => {
        if (!mounted) return;
        setAssignedTeachers(items);
        setError(null);
      })
      .catch((loadError) => {
        if (!mounted) return;
        setError(loadError instanceof Error ? loadError.message : 'Nao foi possivel carregar os professores.');
      })
      .finally(() => {
        if (!mounted) return;
        setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const teachers = useMemo(() => {
    const grouped = new Map<string, AssignedClassTeacher[]>();

    assignedTeachers.forEach((teacher) => {
      const current = grouped.get(teacher.memberId) ?? [];
      grouped.set(teacher.memberId, [...current, teacher]);
    });

    return Array.from(grouped.values()).map((items) => ({
      memberId: items[0].memberId,
      name: items[0].name,
      role: items[0].role,
      status: `${items.length} ${items.length === 1 ? 'turma' : 'turmas'}`,
      classes: items.map((item) => item.className),
    }));
  }, [assignedTeachers]);

  return (
    <MainLayout>
      <div className="space-y-6">
        <header className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Professores</h2>
            <p className="text-sm text-gray-500">Distribuição por turmas e funções ministeriais.</p>
          </div>
        </header>

        {error && (
          <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4 text-sm font-medium text-rose-800">
            {error}
          </div>
        )}

        {isLoading && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm font-medium text-slate-500">
            Carregando professores...
          </div>
        )}

        {!isLoading && teachers.length === 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm font-medium text-slate-500">
            Nenhum professor vinculado a turmas.
          </div>
        )}

        <div className="space-y-4">
          {teachers.map((teacher) => (
            <Card key={teacher.memberId} hoverable className="space-y-4 p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-light text-primary">
                  <Presentation size={22} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900">{teacher.name}</h3>
                  <p className="text-sm text-gray-500">{teacher.role}</p>
                </div>
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
                  {teacher.status}
                </span>
              </div>

              <div className="rounded-2xl bg-gray-50 p-4">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">Turmas vinculadas</p>
                <div className="flex flex-wrap gap-2">
                  {teacher.classes.map((className) => (
                    <span key={className} className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-700">
                      {className}
                    </span>
                  ))}
                </div>
              </div>

              <button className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 py-3 text-sm font-semibold text-slate-700">
                <ShieldCheck size={16} />
                Vínculo via cadastro da turma
              </button>
            </Card>
          ))}
        </div>
      </div>
    </MainLayout>
  );
};

export default TeachersPage;
